import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import moment from 'moment';

import { FlexNotification, FlexPlayers, Player } from '@/modules/league/new-components/types';
import { MyCompletedScheduleLeague } from '@/types/leagues.types';

async function saveCheckin({ matchDateId, player }: { matchDateId: string; player: Player }) {
  try {
    const response = await fetch(`/api/v1/leagues/rsvps/savePlayerCheckin`, {
      method: 'POST',
      body: JSON.stringify({
        MatchDateID: matchDateId,
        UserID_Attendee: player.UserID_1,
        SessionTeamID: player.SessionTeamID,
      }),
    });
    const responseData = await response.json();

    return responseData.data;
  } catch {
    /* empty */
  }
}

async function optInPlayer({
  matchDateId,
  player,
}: {
  matchDateId: string;
  player: Player | MyCompletedScheduleLeague;
}) {
  try {
    // Fetch available spots
    const availableSpotsResponse = await fetch(`/api/v1/leagues/rsvps/getAvailableSpots`, {
      method: 'POST',
      body: JSON.stringify({ MatchDateID: matchDateId }),
    });

    const data = await availableSpotsResponse.json();
    const availableSpots = data.data.availableSpots ?? 0;

    // Check if enough spots are available
    if (availableSpots !== -1 && availableSpots === 0) {
      return {
        message: data.data.message,
        availableSpots,
      };
    }

    const response = await fetch(`/api/v1/leagues/rsvps/saveOptInOutWaitPlayer`, {
      method: 'POST',
      body: JSON.stringify({
        MatchDateID: matchDateId,
        AttendeeSessionID: player.AttendeeSessionID_1,
        SessionTeamID: player.SessionTeamID,
      }),
    });

    const responseData = await response.json();
    return responseData.data;
  } catch {
    return { message: 'An error occurred during the opt-in process', availableSpots: 0 };
  }
}

async function saveStatsReseed({ matchDateId }: { matchDateId: string }) {
  try {
    const response = await fetch(`/api/v1/leagues/rsvps/saveStatsReseedOnRSVP`, {
      method: 'POST',
      body: JSON.stringify({
        MatchDateID: matchDateId,
      }),
    });
    const responseData = await response.json();
    return responseData.data;
  } catch {
    /* empty */
  }
}

async function sendMessage({
  messageText,
  userUuids,
  leagueId,
  slug,
  sessionId,
  matchDateId,
  leagueTitle,
  gameDayTitle,
}: {
  messageText: string;
  userUuids: string[];
  leagueId: string;
  slug: string;
  sessionId: string;
  matchDateId: string;
  leagueTitle: string;
  gameDayTitle: string;
}) {
  try {
    const response = await fetch(`/api/v1/leagues/rsvps/sendMessageToPlayer`, {
      method: 'POST',
      body: JSON.stringify({
        messageText: messageText,
        userUuids: userUuids,
        leagueId: leagueId,
        slug: slug,
        sessionId: sessionId,
        matchDateId: matchDateId,
        leagueTitle: leagueTitle,
        gameDayTitle: gameDayTitle,
      }),
    });
    const responseData = await response.json();
    return responseData.data;
  } catch {
    /* empty */
  }
}

const optInMultiplePlayers = async ({
  matchDateId,
  players,
}: {
  matchDateId: string;
  players: Player[];
}) => {
  if (!players || players.length === 0)
    return { message: 'No players to opt in', availableSpots: 0 };

  try {
    // Fetch available spots
    const response = await fetch(`/api/v1/leagues/rsvps/getAvailableSpots`, {
      method: 'POST',
      body: JSON.stringify({ MatchDateID: matchDateId }),
    });

    const data = await response.json();
    const availableSpots = data.data.availableSpots ?? 0;

    // Check if enough spots are available
    if (availableSpots !== -1 && players.length > availableSpots) {
      return {
        message: `${data.data.message}, but ${players.length} players selected`,
        availableSpots,
      };
    }

    // Opt-in players one by one
    for (const player of players) {
      await fetch(`/api/v1/leagues/rsvps/saveOptInOutWaitPlayer`, {
        method: 'POST',
        body: JSON.stringify({
          MatchDateID: matchDateId,
          UserID: player.UserID_1,
          AttendeeSessionID: player.AttendeeSessionID_1,
          SessionTeamID: player.SessionTeamID,
        }),
      });
    }

    return { message: 'Opted in successfully', availableSpots };
  } catch {
    return { message: 'An error occurred during the opt-in process', availableSpots: 0 };
  }
};

const optOutMultiplePlayers = async ({
  leagueId,
  slug,
  sessionId,
  matchDateId,
  players,
}: {
  leagueId: string;
  slug: string;
  sessionId: string;
  matchDateId: string;
  players: Player[] | MyCompletedScheduleLeague[];
}) => {
  if (players) {
    const playersTeamIDList = players
      .map((player) => player.SessionTeamID)
      .filter(Boolean)
      .map((id) => ({ TeamID: id }));
    try {
      const response = await fetch(`/api/v1/leagues/rsvps/optOutPlayerOfPlayingList`, {
        method: 'POST',
        body: JSON.stringify({
          leagueId: leagueId,
          slug: slug,
          sessionId: sessionId,
          matchDateId: matchDateId,
          playersTeamIDList: playersTeamIDList,
        }),
      });

      const responseData = await response.json();
      return responseData.data;
    } catch {
      return { message: 'An error occurred during the opt-out process' };
    }
  }
};

const removeMultiplePlayers = async ({
  matchDateId,
  players,
  doing,
}: {
  matchDateId: string;
  players: Player[];
  doing: string;
}) => {
  if (players) {
    try {
      for (const player of players) {
        await fetch(`/api/v1/leagues/rsvps/saveRemovePlayerFromWaitlist`, {
          method: 'POST',
          body: JSON.stringify({
            MatchDateID: matchDateId,
            UserID: player?.UserID_1,
            AttendeeSessionID: player?.AttendeeSessionID_1,
            SessionTeamID: player?.SessionTeamID,
            doing: doing,
          }),
        });
      }
      return {
        message: `Players are successfully ${doing === 'out' ? 'removed from' : 'added to'} waitlist.`,
      };
    } catch {
      /* empty */
    }
  }
  return { message: 'No players provided.' };
};

async function addOrRemoveFromWaitlist({
  matchDateId,
  player,
  doing,
}: {
  matchDateId: string;
  player: Player | MyCompletedScheduleLeague;
  doing: string;
}) {
  try {
    const response = await fetch(`/api/v1/leagues/rsvps/saveRemovePlayerFromWaitlist`, {
      method: 'POST',
      body: JSON.stringify({
        MatchDateID: matchDateId,
        UserID: player.UserID_1,
        AttendeeSessionID: player.AttendeeSessionID_1,
        SessionTeamID: player.SessionTeamID,
        doing: doing,
      }),
    });
    const responseData = await response.json();
    return responseData.data;
  } catch {
    /* empty */
  }
}

export const useSaveStatsReseed = () => useMutation(saveStatsReseed);
export const useSaveCheckin = () => useMutation(saveCheckin);
export const useSaveOptIn = () => useMutation(optInPlayer);
export const useSaveOptMultiplePlayers = () => useMutation(optInMultiplePlayers);
export const useSaveOptOutMultiplePlayers = () => useMutation(optOutMultiplePlayers);
export const useSaveRemoveMultiplePlayers = () => useMutation(removeMultiplePlayers);
export const useSendMessage = () => useMutation(sendMessage);
export const useWaitlist = () => useMutation(addOrRemoveFromWaitlist);

interface EmailPayloadForGroupSplits {
  uuids: string[];
  listOfPlayers: string[];
  body: string;
  title: string;
}
interface EmailPayloadForOptInReminder {
  uuids: string[];
  body: string;
  title: string;
}

export const useGetFlexNotificationAndResendOptInReminder = (
  eventTitle: string,
  sessionId: string,
  matchDateId: string,
  slug: string
) => {
  const { NEXT_PUBLIC_PL_URL } = process.env;

  return useMutation({
    mutationFn: () => {
      return axios.post<FlexNotification>(
        '/api/v1/leagues/rsvps/getFlexNotificationsAndResendOptInReminder',
        {
          MatchDateID_OptInOutReminder: matchDateId,
        }
      );
    },
    onSuccess: async (data) => {
      const flexNotificationsData = data?.data.secondDatatable;
      const emailPayload: EmailPayloadForOptInReminder = {
        uuids: [],
        body: `Future schedule opt-in / opt-out reminder for [DateSpan].
         You are currently opted [OptInOrOut].
          If you wish to change this status please do so prior to the next week.
          ${NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${sessionId}/matchdate/${matchDateId}/rsvp`,
        title: `${eventTitle}`,
      };
      if (flexNotificationsData?.length) {
        // Collect the UUIDs from flexNotificationsData
        for (const player of flexNotificationsData) {
          emailPayload.uuids.push(player.UserID_1);
          emailPayload.body.replace('[OptInOrOut]', ` ${player.IsOptedIn === 1 ? ' IN' : ' OUT'}`);
          emailPayload.body.replace(
            '[DateSpan]',
            ` ${moment.utc(player?.FromDateTime, 'YYYY-MM-DD[T]hh:mm:ss[Z]').toDate()} to ${moment.utc(player?.ToDateTime, 'YYYY-MM-DD[T]hh:mm:ss[Z]').toDate()}
						`
          );
        }
        // Send the email to all players in one API call
        await axios.post('/v1/leagues/send-email', emailPayload);
      }
    },
  });
  // loop through flexNotificationsData and send email to each player
  // const emailTemplate = `
  //   ${Event.Title}
  //   Future schedule opt-in / opt-out reminder for ${DateSpan}.
  //   You are currently opted ${OptInOrOut}.
  //   If you wish to change this status please do so prior to the next week.
  //   ${URLToLeague}
  // `;
};

export const useGetFlexNotificationAndResendGroupSplitEmails = (
  eventTitle: string,
  sessionId: string,
  matchDateId: string,
  slug: string
) => {
  const { NEXT_PUBLIC_PL_URL } = process.env;

  return useMutation({
    mutationFn: () => {
      return axios.post<FlexNotification>(
        '/api/getLeagues/getFlexNotificationsAndResendGroupSplit',
        {
          MatchDateID_NewGroupInfo: matchDateId,
        }
      );
    },
    onSuccess: async (data) => {
      const flexNotificationsData = data?.data.firstDatatable;
      const flexPlayers: FlexPlayers[] = [];
      const emailPayload: EmailPayloadForGroupSplits = {
        uuids: [],
        listOfPlayers: [],
        body: `All matches from the prior flex date range have been calculated. Here are the players in your group for the date range of ${data?.data?.firstDatatable[0]?.FromDateTime} to ${data?.data?.firstDatatable[0]?.ToDateTime}, and their contact information. It is the responsibility of you and the other players in this group to contact one another for game play and enter scores prior to that date. Periodic reminders will be sent to groups that have not entered scores. Games without scores recorded by ${data?.data?.firstDatatable[0]?.ToDateTime} will be marked as canceled.
        ${NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${sessionId}/matchdate/${matchDateId}/rsvp
        [PoolAndScore]
        [PlayerList]
        `,
        title: `${eventTitle}`,
      };
      if (flexNotificationsData.length > 0) {
        for (const row of flexNotificationsData) {
          const flexPlayersResponse = await axios.post<FlexPlayers>(
            '/api/getLeagues/getFlexPlayers',
            {
              MatchDateID: row.MatchDateID || matchDateId,
            }
          );
          flexPlayers.push(flexPlayersResponse.data);
        }
        if (flexPlayers.length) {
          for (const player of flexPlayers) {
            emailPayload.uuids.push(player.UserID_1);
            emailPayload.listOfPlayers.push(`${player.FirstName_1} ${player.LastName_1}`);
            emailPayload.body.replace(
              '[PoolAndScore]',
              `Group: ${player.PoolNumber} Count in Group: ${player.NumberOfTeamsInPool}`
            );
          }
          emailPayload.body.replace('[PlayerList]', `${emailPayload.listOfPlayers}`);
        }
        // Send the email to all players in one API call
        await axios.post('/v1/leagues/send-email', emailPayload);
      }
    },
  });
  //when you have all flex players, loop through flex players and send email to each player
  // const emailTemplate = `
  //   ${Event.Title}
  //   All matches from the prior flex date range have been calculated. Here are the players in your group for the date range of [ ${row.FromDateTime} to  ${row.ToDateTime}, and their contact information. It is the responsibility of you and the other players in this group to contact one another for game play and enter scores prior to that date. Periodic reminders will be sent to groups that have not entered scores.
  //   Games without scores recorded by [ToDateTime] will be marked as canceled.
  //   ${URLToLeague}
  //   ${PoolAndScore}
  //   ${PlayerList}
  // `;
};
